import React, { Component } from "react";
import { identity } from "lib";
import endcapData from "./endcapData";
import { Endcap } from "./Endcap";
import { getLineType } from "./LineUtils";
import {
  getLineBoundingBox,
  toLocalSvgCoordinates
} from "./lineCoordinateUtils";
import {
  getCombinedAnimatedStyles,
  shouldAnimateElement
} from "lib/animationUtils";

class Line extends Component {
  static defaultProps = {
    connectDragSource: identity
  };

  constructor(props) {
    super(props);

    this.handleClick = this.handleClick.bind(this);
    this.onSelect = this.onSelect.bind(this);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleDocumentMouseMove = this.handleDocumentMouseMove.bind(this);
    this.handleDocumentMouseUp = this.handleDocumentMouseUp.bind(this);
    this.getEndcaps = this.getEndcaps.bind(this);
    this.getStrokeLineCap = this.getStrokeLineCap.bind(this);

    this.wrapperRef = React.createRef();

    // Manual drag state (react-dnd doesn't work well with SVG elements)
    this.isDragging = false;
    this.dragStartPos = null;
    this.hasDraggedPastThreshold = false;
    this.canDrag = true;

    this.state = {
      isHovered: false,
      svgRenderObject: null,
      forceUpdateInterval: null,
      renderVersion: ""
    };
  }

  componentWillUnmount() {
    // Clean up any lingering event listeners
    window.removeEventListener("mousemove", this.handleDocumentMouseMove);
    window.removeEventListener("mouseup", this.handleDocumentMouseUp);
  }

  onSelect(e) {
    e.stopPropagation();
    e.preventDefault();

    // Don't re-select after a drag — handleLineDragEnd already handles selection
    if (this.justFinishedDrag) {
      this.justFinishedDrag = false;
      return;
    }

    /* if user pressed shift or meta/command(mac), we append the to the selection */
    const append = e.shiftKey || e.metaKey;
    this.props.onSelectItem({ append });
  }

  stopPropagationAndPreventDefault(e) {
    e.stopPropagation();
    e.preventDefault();
  }

  handleClick = (event, domId) => {
    this.stopPropagationAndPreventDefault(event);
    this.onSelect(event);
  };

  handleMouseEnter = () => {
    if (!this.state.isHovered) {
      this.setState({ isHovered: true });
    }
  };

  handleMouseLeave = () => {
    if (this.state.isHovered) {
      this.setState({ isHovered: false });
    }
  };

  handleMouseDown = e => {
    // Prevent browser's default drag-to-select behavior
    // This doesn't block the click event from firing
    e.preventDefault();

    // Store whether dragging is allowed (locked elements can be selected but not dragged)
    this.canDrag = !this.props.isAllRestrictionsLocked;

    // Alt/Option at mousedown commits the drag as a duplicate (Figma parity).
    // Modifier state at mousedown is the deciding signal — releasing Alt
    // mid-drag does not flip the operation.
    this.altKeyOnDragStart = !!e.altKey;

    // Start tracking for potential drag
    this.isDragging = true;
    this.hasDraggedPastThreshold = false;
    this.dragStartPos = {
      x: e.clientX,
      y: e.clientY
    };

    // Add window-level listeners to track mouse movement
    window.addEventListener("mousemove", this.handleDocumentMouseMove);
    window.addEventListener("mouseup", this.handleDocumentMouseUp);
  };

  handleDocumentMouseMove = e => {
    if (!this.isDragging || !this.dragStartPos) return;

    // Don't allow drag if element is locked
    if (!this.canDrag) return;

    const deltaX = e.clientX - this.dragStartPos.x;
    const deltaY = e.clientY - this.dragStartPos.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Only start actual drag after moving past threshold (5px)
    // Using 5px to avoid accidental drags from hand tremor during clicks
    if (!this.hasDraggedPastThreshold && distance > 5) {
      this.hasDraggedPastThreshold = true;

      // Prevent browser's default drag-to-select behavior once we've started dragging
      e.preventDefault();

      // Notify that dragging has started (for visual feedback like hiding the element)
      if (this.props.onDragStart) {
        this.props.onDragStart({ altKey: this.altKeyOnDragStart });
      }
    }

    if (this.hasDraggedPastThreshold) {
      // Prevent default on every move during drag
      e.preventDefault();

      // Calculate movement delta accounting for zoom
      const zoom = this.props.zoom || 1;
      if (this.props.onDragMove) {
        this.props.onDragMove({
          deltaX: deltaX / zoom,
          deltaY: deltaY / zoom,
          shiftKey: e.shiftKey,
          altKey: this.altKeyOnDragStart
        });
      }
    }
  };

  handleDocumentMouseUp = e => {
    // Remove listeners
    window.removeEventListener("mousemove", this.handleDocumentMouseMove);
    window.removeEventListener("mouseup", this.handleDocumentMouseUp);

    if (!this.isDragging) return;

    const wasDragging = this.hasDraggedPastThreshold;

    // Reset drag state
    this.isDragging = false;
    this.hasDraggedPastThreshold = false;

    if (wasDragging && this.canDrag) {
      // Finalize the drag - calculate final position
      const deltaX = e.clientX - this.dragStartPos.x;
      const deltaY = e.clientY - this.dragStartPos.y;
      const zoom = this.props.zoom || 1;

      // Prevent the subsequent click event from re-selecting (which would
      // deselect other elements in a multi-selection)
      this.justFinishedDrag = true;

      if (this.props.onDragEnd) {
        this.props.onDragEnd({
          deltaX: deltaX / zoom,
          deltaY: deltaY / zoom,
          shiftKey: e.shiftKey,
          altKey: this.altKeyOnDragStart
        });
      }
      // Note: onDragEnd in DraggableElement handles selection after drag
    }
    // For clicks (no drag), onClick handler on the SVG line handles selection

    this.dragStartPos = null;
  };

  getEndcaps(position) {
    const { endcap } = this.props.elementData;

    if (endcap[position] === "none") {
      return;
    }

    const data = endcapData[endcap[position]]?.[position];
    if (!data) {
      return;
    }

    const endCapProps = {
      ...data,
      position,
      uniqueId: this.props.elementData.uniqueId,
      color: this.props.elementData.color,
      fill: data.fill !== "none" ? this.props.elementData.color : "none",
      strokeLinecap: this.props.elementData.strokeLinecap,
      strokeLinejoin: this.props.elementData.strokeLinecap
    };

    return <Endcap {...endCapProps} />;
  }

  getStrokeLineCap() {
    const { strokeLinecap } = this.props.elementData;
    if (
      strokeLinecap === "round" ||
      getLineType(this.props.elementData.strokeDashArray) === "Dot"
    ) {
      return "round";
    } else if (strokeLinecap === "butt") {
      return "butt";
    } else {
      return "square";
    }
  }

  render() {
    const { opacity, thickness: rawThickness = 1 } = this.props.elementData;
    // Ensure minimum visible stroke width of 1px
    const thickness = Math.max(rawThickness, 1);

    const { isSelected, isAllRestrictionsLocked } = this.props;

    const coordinates = this.props.elementData.coordinates;
    const { x1, y1, x2, y2 } = coordinates;

    const isHovered = this.state.isHovered;

    // Calculate padding for stroke width to ensure full visibility
    // Include extra space for endcaps if present
    const hasEndcaps =
      this.props.elementData.endcap?.start !== "none" ||
      this.props.elementData.endcap?.end !== "none";
    const endcapPadding = hasEndcaps ? thickness * 2 : 0;
    const padding = Math.max(thickness / 2, 1) + endcapPadding;

    // Calculate bounding box and local SVG coordinates
    const boundingBox = getLineBoundingBox(x1, y1, x2, y2, padding);
    const { localX1, localY1, localX2, localY2 } = toLocalSvgCoordinates(
      x1,
      y1,
      x2,
      y2,
      padding
    );

    // Minimum dimensions for selectability
    const minDimension = 20;
    const svgWidth = Math.max(boundingBox.width, minDimension);
    const svgHeight = Math.max(boundingBox.height, minDimension);

    // Hit area stroke width (invisible stroke for easier selection)
    const hitAreaStrokeWidth = Math.max(thickness + 10, 15);

    const lineElement = (
      <div
        style={{
          overflow: "visible",
          width: `${svgWidth}px`,
          height: `${svgHeight}px`,
          zIndex: isHovered || isSelected ? 1000 : 0,
          pointerEvents: "none"
        }}
      >
        <svg
          style={{
            width: "100%",
            height: "100%",
            overflow: "visible",
            pointerEvents: "none"
          }}
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
        >
          <defs>
            {this.getEndcaps("end")}
            {this.getEndcaps("start")}
          </defs>
          <g>
            {/* Invisible hit detection stroke - uses pointer-events="stroke" to only
                capture events on the stroke path, allowing clicks to pass through
                empty areas to elements behind */}
            <line
              x1={localX1}
              y1={localY1}
              x2={localX2}
              y2={localY2}
              stroke="transparent"
              strokeWidth={hitAreaStrokeWidth}
              onClick={this.onSelect}
              onMouseDown={this.handleMouseDown}
              onMouseEnter={this.handleMouseEnter}
              onMouseLeave={this.handleMouseLeave}
              style={{
                pointerEvents: "stroke",
                cursor: "move"
              }}
            />
            {/* Visible line */}
            <line
              x1={localX1}
              y1={localY1}
              x2={localX2}
              y2={localY2}
              stroke={this.props.elementData.color}
              opacity={opacity}
              strokeWidth={thickness}
              strokeDasharray={this.props.elementData.strokeDashArray}
              strokeLinecap={this.getStrokeLineCap()}
              markerEnd={
                this.props.elementData.endcap.end !== "none"
                  ? `url(#endcap-end-${this.props.elementData.uniqueId})`
                  : undefined
              }
              markerStart={
                this.props.elementData.endcap.start !== "none"
                  ? `url(#endcap-start-${this.props.elementData.uniqueId})`
                  : undefined
              }
              style={{ pointerEvents: "none" }}
            />
            {/* Hover highlight */}
            {isHovered && !isAllRestrictionsLocked && !isSelected && (
              <line
                x1={localX1}
                y1={localY1}
                x2={localX2}
                y2={localY2}
                stroke={"#3184FC"}
                style={{
                  pointerEvents: "none",
                  strokeWidth: Math.max(thickness + 2, 2)
                }}
              />
            )}
          </g>
        </svg>
      </div>
    );

    const shouldAnimate = shouldAnimateElement(
      this.props.elementData,
      this.props.isPlaying,
      this.props.isPaused || false
    );
    if (shouldAnimate) {
      const animationWrapperStyle = getCombinedAnimatedStyles(
        { position: "relative" },
        this.props.elementData,
        this.props.isPlaying,
        this.props.pageId,
        this.props.zoom,
        this.props.currentPlayingTime || 0,
        this.props.isPaused || false
      );

      return <div style={animationWrapperStyle}>{lineElement}</div>;
    }

    return lineElement;
  }
}

export default Line;
